import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteButton from "../common/DeleteButton";
import ThreeDot from "../common/ThreeDot";
import EditButton from "../common/EditButton";
import {
  deleteAgent,
  getAllAgents,
  updateAgent,
} from "../../services/agent.service";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import ViewDetailsButton from "../common/ViewDetailsButton";

export default function Agent() {
  const { token } = useSelector((state) => state?.auth);
  const [agents, setAgents] = useState([]);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(null);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);

  const fetchAgents = async (page = 1) => {
    try {
      const params = { page };
      const res = await getAllAgents(token, params);
      if (res && res.status >= 200 && res.status < 300) {
        // console.log(res.data);

        setAgents(res?.data?.results);
        setNext(res?.data?.next);
        setPrevious(res?.data?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.count || null);
        setTotalPages(Math.ceil((res?.data?.count || 0) / itemsPerPage));
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch categories. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  useEffect(() => {
    fetchAgents(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const startEntry = (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  const handleStatusChange = async (item) => {
    const { status, customer_id, fullname } = item;

    const updateAgentStatus = async (action) => {
      const data = {
        status: action,
      };

      try {
        const res = await updateAgent(token, data, customer_id);

        if (res && res.status >= 200 && res.status < 300) {
          await Swal.fire({
            title: "Success",
            text: `${fullname} Agent has been ${
              action === "active"
                ? "Activated"
                : action === "deactive"
                ? "Deactivated"
                : "Rejected"
            } successfully.`,
            icon: "success",
            confirmButtonText: "OK",
          }).then(() => {
            fetchAgents(currentPage);
          });
        } else if (res && res.status >= 400 && res.status < 500) {
          await Swal.fire(
            "Error",
            res?.data?.message || "Invalid input. Please check your data.",
            "error"
          );
        } else {
          await Swal.fire(
            "Error",
            res?.data?.error ||
              "An unexpected error occurred. Please try again later.",
            "error"
          );
        }
      } catch (error) {
        console.error(`Error during ${action}ing agent:`, error);
        await Swal.fire(
          "Error",
          error?.response?.data?.error ||
            "A network or server error occurred. Please try again later.",
          "error"
        );
      }
    };

    if (status === "pending") {
      const result = await Swal.fire({
        title: "Change Agent Status",
        text: "Do you want to accept or reject this agent?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Accept",
        cancelButtonText: "Reject",
      });

      if (result.isConfirmed) {
        await updateAgentStatus("active");
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        await updateAgentStatus("rejected");
      }
    } else if (status === "active") {
      const result = await Swal.fire({
        title: "Deactivate Agent",
        text: "Are you sure you want to deactivate this agent?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Deactivate",
        cancelButtonText: "No",
      });

      if (result.isConfirmed) {
        await updateAgentStatus("deactive");
      }
    } else if (status === "rejected") {
      const result = await Swal.fire({
        title: "Accept Agent",
        text: "Do you want to accept this rejected agent?",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Yes, Accept",
        cancelButtonText: "No",
      });

      if (result.isConfirmed) {
        await updateAgentStatus("active");
      }
    } else if (status === "deactive") {
      const result = await Swal.fire({
        title: "Activate Agent",
        text: "Do you want to activate this deactivated agent?",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Yes, activate",
        cancelButtonText: "No",
      });

      if (result.isConfirmed) {
        await updateAgentStatus("active");
      }
    } else {
      console.log("Unknown status");
    }
  };

  const handleDelete = async (agentName, agentID) => {
    try {
      const res = await deleteAgent(token, agentID);
      // console.log(res);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Deleted!",
          text: `${agentName} Agent has been deleted successfully.`,
          icon: "success",
        }).then(() => {
          const remainingItems = agents.length - 1;

          if (remainingItems === 0 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
            fetchAgents(currentPage - 1);
          } else {
            fetchAgents(currentPage);
          }
        });
      } else {
        Swal.fire(
          "Error",
          res?.data?.detail || "Failed to delete the Agent. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during Agent deletion:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  return (
    <div className="content-body">
      {/* <!-- row --> */}
      <div className="container-fluid">
        <div className="form-head d-flex mb-3 align-items-start justify-content-between">
          {/* <div className="input-group search-area d-inline-flex">
            <input
              type="text"
              className="form-control"
              placeholder="Search here"
            />
            <div className="input-group-append">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </div> */}
          <div className="m-4"></div>
          <div style={{ zIndex: 2 }}>
            <Link to="/agent/agent-add">
              <button className="btn btn-primary px-3 py-2 add-btn sticky-btn">
                Add
                <img
                  className="plus-sign ms-2"
                  src="/assets/images/kidskart/icon/add.png"
                />
              </button>
            </Link>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-12 col-xxl-12 col-lg-12 col-sm-12">
            <div className="card">
              <div className="card-body p-0">
                {agents && agents.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table order-request2">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Agent</th>
                          <th>Code</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Orders</th>
                          <th>Sales</th>
                          {/* <th>Status</th> */}
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {agents.map((item, i) => (
                          <tr key={item.customer_id}>
                            <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                            <td>{item.fullname}</td>
                            <td>{item.user_code}</td>
                            <td>{item.email}</td>
                            <td>{item.mobile_number}</td>
                            <td>{item.agent_total_orders || 0}</td>
                            <td>
                              ₹
                              {(item.agent_total_sales * 1)?.toLocaleString(
                                "en-IN",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              ) || 0}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {/* <Link
                                  className={`${
                                    item.status === "pending"
                                      ? "btn-warning"
                                      : item.status === "active"
                                      ? "btn-success"
                                      : "btn-danger"
                                  } btn light btn-sm`}
                                  onClick={() => handleStatusChange(item)}
                                >
                                  {item.status.toString().toUpperCase()}
                                </Link> */}
                                <div className="dropdown ms-auto c-pointer">
                                  <ThreeDot />
                                  <div className="dropdown-menu dropdown-menu-end">
                                    <EditButton
                                      to={`/agent/agent-update`}
                                      id={item.customer_id}
                                    />
                                    <DeleteButton
                                      name={`${item.fullname} Agent`}
                                      onDelete={() =>
                                        handleDelete(
                                          item.fullname,
                                          item.customer_id
                                        )
                                      }
                                    />
                                    <ViewDetailsButton
                                      to={`/agent/agent-details`}
                                      id={item.customer_id}
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {agents && agents.length < 5 ? (
                          <tr style={{ height: "205px" }}></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="my-5 py-5 text-center fs-4 fw-bold">
                    Please Add Agent
                  </div>
                )}
              </div>
            </div>

            {/* Pagination Info */}
            {totalEntries && totalEntries > 10 && (
              <div className="dataTables_wrapper no-footer">
                <div className="dataTables_info">
                  Showing {startEntry} to {endEntry} of {totalEntries} entries
                </div>
                <div className="dataTables_paginate paging_simple_numbers">
                  <button
                    className={`btn btn-primary me-2 ${
                      !previous && "disabled"
                    }`}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    {(() => {
                      const maxVisible = 7;
                      const pages = Array.from(
                        { length: totalPages },
                        (_, i) => i + 1
                      );
                      let start = Math.max(
                        currentPage - Math.floor(maxVisible / 2),
                        1
                      );
                      let end = start + maxVisible - 1;

                      if (end > totalPages) {
                        end = totalPages;
                        start = Math.max(end - maxVisible + 1, 1);
                      }

                      return pages.slice(start - 1, end).map((page) => (
                        <button
                          key={page}
                          className={`btn btn-sm mx-1 ${
                            currentPage === page
                              ? "btn-dark"
                              : "btn-outline-dark"
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                  </span>
                  <button
                    className={`btn btn-primary ms-2 ${!next && "disabled"}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
